import React, { FC, useEffect, useMemo, useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import {
  Container,
  Header,
  StyledTableHead,
  StyledTablePagination,
  StyledTableRow,
  Title,
} from 'containers/CourtManagerAssignedExpertise/style';
import messages from 'containers/ProvisionalClaimsPage/messges';
import { translate } from 'containers/services/translate';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import { get } from 'lodash';
import { ProvisionalClaim } from 'containers/ProvisionalClaimsPage/typings';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import AddProvisionalClaimDrawer from 'containers/ProvisionalClaimsPage/AddProvisionalClaimDrawer';
import { makeSelectListings } from 'containers/AppContainer/selectors';
import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import { useDispatch, useSelector } from 'react-redux';
import { requestCorporealLawyers } from 'containers/RecapManagerPage/actions';
import { makeSelectCorporealLawyers } from 'containers/RecapManagerPage/selectors';
import {
  getListBySagisKey,
  getBawabaLawyers,
} from 'containers/RecapManagerPage/utils';
import { LISTINGS } from 'containers/data-structure';
import { ITEM_KEYS, SAGIS_KEYS } from 'containers/RecapManagerPage/constants';
import { requestGetProvisionalClaim } from 'containers/ProvisionalClaimsPage/actions';
import { makeSelectProvisionalClaim } from 'containers/ProvisionalClaimsPage/selectors';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import { resetProvisionalClaimDetailsStore } from 'containers/ProvisionalClaimRecap/actions';
import { isLawyer } from 'containers/AppContainer/utils';
import { PROVISIONAL_STATUS_MESSAGES } from 'containers/ProvisionalClaimsPage/constants';

export interface ProvisionalClaimsPageProps {
  intl: IntlShape;
}

const ProvisionalClaimsPage: FC<ProvisionalClaimsPageProps> = (
  props,
): JSX.Element => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openedAddDrawer, setOpenedAddDrawer] = useState(false);
  const tableHeaderTitles = useMemo(
    () => [
      translate(
        messages.monitoringProvisionalClaimTableHeaderTitlesReference,
        props,
      ),
      translate(
        messages.monitoringProvisionalClaimTableHeaderTitlesCourtNumber,
        props,
      ),
      translate(
        messages.monitoringProvisionalClaimTableHeaderTitlesCreationDate,
        props,
      ),
      translate(
        messages.monitoringProvisionalClaimTableHeaderTitlesStatus,
        props,
      ),
    ],
    [props],
  );
  const listings: Listings = useSelector(makeSelectListings());
  const lawyers = useSelector(makeSelectCorporealLawyers());
  const provisionalClaims = useSelector(makeSelectProvisionalClaim());
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  useEffect(() => {
    dispatch(requestCorporealLawyers());
  }, []);

  useEffect(() => {
    dispatch(requestGetProvisionalClaim(page, rowsPerPage));
  }, [page, rowsPerPage]);

  const corpoLawyers = useMemo(() => {
    const _stackholders = getListBySagisKey(
      get(listings, LISTINGS.STACKHOLDER_TYPE, []),
      get(listings, LISTINGS.STACKHOLDERS, []),
      ITEM_KEYS.LAWYER,
      SAGIS_KEYS.ITEM_TYPE as keyof SagisConfig,
    );

    return getBawabaLawyers(_stackholders, lawyers);
  }, [listings, lawyers]);
  const handleToggleDrawer = () => {
    setOpenedAddDrawer(false);
  };
  const handleredirectToProvisionalClaimRecap = (
    provisionalClaimId: number,
  ) => {
    dispatch(resetProvisionalClaimDetailsStore());
    isLawyer()
      ? pushTo(`${PAGES.PROVISIONAL_CLAIMS_RECAP}/${provisionalClaimId}`)()
      : pushTo(
          `${PAGES.COURT_MONITORING_PROVISIONAL_CLAIMS_RECAP}/${provisionalClaimId}`,
        )();
  };
  return (
    <>
      <Container>
        <Header>
          <Title>
            {translate(messages.monitoringProvisionalClaimTitle, props)}
          </Title>
          <PrimaryButton
            background={'#0075C9'}
            titlecolor={'#FFFFFF'}
            border={'#0075C9'}
            className="add-content-bloc-button"
            title={translate(
              messages.monitoringProvisionalClaimAddDrawerTitle,
              props,
            )}
            onClick={() => {
              setOpenedAddDrawer(true);
            }}
            disabled={false}
          />
        </Header>
        <TableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                {tableHeaderTitles.map(title => (
                  <TableCell key={title}>{title}</TableCell>
                ))}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {get(provisionalClaims, 'provisionalClaims', []).map(
                (row: ProvisionalClaim) => {
                  return (
                    <StyledTableRow
                      data-testid="provisional-claim-row"
                      key={row.id}
                      onClick={() =>
                        handleredirectToProvisionalClaimRecap(row.id)
                      }
                    >
                      <TableCell>{get(row, 'reference', '')}</TableCell>
                      <TableCell>{get(row, 'courtNumber', '')}</TableCell>
                      <TableCell>
                        {new Date(
                          get(row, 'createdAt', ''),
                        ).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {translate(
                          { ...get(PROVISIONAL_STATUS_MESSAGES, row?.status) },
                          props,
                        )}
                      </TableCell>
                    </StyledTableRow>
                  );
                },
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <StyledTablePagination
          rowsPerPageOptions={[5, 10, 25]}
          count={get(provisionalClaims, 'provisionalClaimsCount', 0)}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={translate(messages.rowsPerPage, props)}
        />
      </Container>
      {openedAddDrawer && (
        <AddProvisionalClaimDrawer
          handleToggleDrawer={handleToggleDrawer}
          listings={listings}
          lawyers={corpoLawyers}
          isDrawerOpen={openedAddDrawer}
        />
      )}
    </>
  );
};

export default injectIntl(ProvisionalClaimsPage);
